<template>
  <div class="analytic-report aircraft-cameras-report">
    <h1 class="aircraft-cameras-report__title">
      {{ $t('aircraftReport') }}
    </h1>
    <div class="analytic-report__settings">
      <SmartInputDate
        v-model="archiveFrom"
        :config-flat-pickr="{maxDate: new Date()}"
        :caption="$t('startDate')"
        :locale="locale"
        class="analytic-report__settings-control analytic-report__settings-control_small"
      />
      <SmartInputDate
        v-model="archiveTo"
        :config-flat-pickr="{minDate: archiveFrom, maxDate: new Date()}"
        :caption="$t('endDate')"
        :locale="locale"
        class="analytic-report__settings-control analytic-report__settings-control_small"
      />
      <SmartInputText
        v-model="searchText"
        :caption="$t('search')"
        class="analytic-report__settings-control"
      />
      <CamsButton type="button" class="analytic-report__settings-control" @click="openCameraSelection">
        {{ $t('selectCameras') }} ({{ selectedCameraNumbers.length }})
      </CamsButton>
      <CamsButton type="button" class="analytic-report__settings-control" @click="resetFilters">
        {{ $t('reset') }}
      </CamsButton>
      <CamsButton
        class="analytic-report__settings-control"
        priority="primary"
        type="button"
        :disabled="!canLoadReport || isExporting"
        @click="openDownloadReportScreen()"
      >
        {{ $t('downloadReport') }}
      </CamsButton>
    </div>
    <div class="aircraft-cameras-report__sources">
      <SmartSwitch v-model="videoEnabled" :caption="$t('videoEvents')" />
      <SmartSwitch v-model="audioEnabled" :caption="$t('audioEvents')" />
    </div>

    <SpinnerLoading v-if="isLoadingReport" class="loader_center" color="blue" />
    <div v-else-if="loadError" class="analytic-report__no-data" role="alert">
      <p>{{ loadError }}</p>
      <CamsButton type="button" @click="loadReport">
        {{ $t('retry') }}
      </CamsButton>
    </div>
    <p v-else-if="!selectedCameraNumbers.length" class="analytic-report__no-data">
      {{ $t('selectCameras') }}
    </p>
    <p v-else-if="!hasValidDates" class="analytic-report__no-data">
      {{ $t('invalidDate') }}
    </p>
    <p v-else-if="!messages.length" class="analytic-report__no-data">
      {{ $t('noEvents') }}
    </p>
    <template v-else>
      <div class="analytic-report__pagination">
        <paginate
          v-model="currentPage"
          class="analytic-report__pagination_smart-pagination"
          :page-count="allPages"
          :page-range="5"
          active-class="smart-pagination__page_active"
          break-view-class="smart-pagination__page_collapse"
          container-class="smart-pagination"
          next-class="smart-pagination__page smart-pagination__next-prev"
          next-link-class="smart-pagination__page-link small"
          next-text="<svg class='icon icon-arrow-left'><use xlink:href='#icon-arrow-left'></use></svg>"
          page-class="smart-pagination__page"
          page-link-class="smart-pagination__page-link small"
          prev-class="smart-pagination__page smart-pagination__next-prev"
          prev-link-class="smart-pagination__page-link small"
          prev-text="<svg class='icon icon-arrow-right'><use xlink:href='#icon-arrow-right'></use></svg>"
        />
        <SmartSelect
          v-model="pageSize"
          :options="[20, 40, 60, 100, 200]"
          :caption="$t('itemsPerPage')"
          width="fill"
          class="analytic-report__pagination_smart-select-pagination"
          size="s"
        />
      </div>
      <div class="analytic-report__table cams-table-wrapper">
        <table class="cams-table">
          <thead>
            <tr>
              <th class="cams-table__cell cams-table__cell_fixed-width" />
              <th class="cams-table__cell">
                {{ $t('cameraNumber') }}
              </th>
              <th class="cams-table__cell">
                {{ $t('cameraAddress') }}
              </th>
              <th class="cams-table__cell">
                {{ $t('text') }}
              </th>
              <th class="cams-table__cell">
                {{ $t('eventType') }}
              </th>
              <th class="cams-table__cell">
                {{ $t('date') }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="message in messages" :key="`${message.cameraNumber}-${message.id}`" :class="{'warning warning-bg': message.alarm}">
              <td class="cams-table__cell cams-table__cell_fixed-width">
                <CamsButton
                  type="button"
                  icon-type="only"
                  :title="$t('eventVideo')"
                  :disabled="isLoadingVideo"
                  @click="openEventVideo(message)"
                >
                  <svg><use xlink:href="#icon-video-preview" /></svg>
                </CamsButton>
              </td>
              <td class="cams-table__cell">
                {{ message.cameraNumber }}
              </td>
              <td class="cams-table__cell">
                {{ cameraTitle(message.cameraNumber) }}
              </td>
              <td class="cams-table__cell">
                {{ message.text || $t('aircraftEvent') }}
              </td>
              <td class="cams-table__cell">
                <AircraftEventTypeLabel :source-type="message.source_type" />
              </td>
              <td class="cams-table__cell">
                {{ message.date | localDateTime }}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </template>
  </div>
</template>

<script>
import AircraftEventTypeLabel from "@/components/pacs/analytics/aircraft/AircraftEventTypeLabel.vue";
import AircraftReportCamerasDialog from "@/components/pacs/analytics/aircraft/AircraftReportCamerasDialog.vue";
import {ACTION_LOAD_AIRCRAFT_REPORT} from "@/store/analytics/aircraft/index.js";
import {ACTION_LOAD_CAMERAS, ACTION_LOAD_CAMERA_FOR_PLAYER, FIELDS_CAMERA} from "@/store/cameras/index.js";
import {eventHandlersMixin} from "@/components/events/mixins.js";
import {downloadReportMixin} from "@/components/oneScreen/reports/downloadReportMixin.js";
import {downloadCSV} from "@/utils/helpers.js";
import {TOKEN_TTL} from "@/utils/consts.js";
import {QUERY_KEY_AIRCRAFT_REPORT_CAMERA_NUMBERS} from "@/router/queryKeys.js";

const REPORT_ACTION = `analytics/aircraft/${ACTION_LOAD_AIRCRAFT_REPORT}`;
const MAX_EXPORT_ROWS = 100000;

/** Отдельная страница отчёта по событиям ЛА с выбранных камер карты. */
export default {
  name: "AircraftCamerasReport",
  components: {AircraftEventTypeLabel},
  mixins: [eventHandlersMixin, downloadReportMixin],
  props: {
    initialCameraNumbers: {type: Array, default: () => []},
  },
  data() {
    const archiveTo = new Date();
    return {
      selectedCameraNumbers: [...new Set(this.initialCameraNumbers.map(String))],
      camerasByNumber: {},
      archiveFrom: new Date(archiveTo.getTime() - 60 * 60 * 1000),
      archiveTo,
      searchText: "",
      videoEnabled: true,
      audioEnabled: true,
      currentPage: 1,
      pageSize: 60,
      allPages: 1,
      messages: [],
      isLoadingReport: false,
      isLoadingVideo: false,
      isExporting: false,
      loadError: "",
      requestVersion: 0,
      isReportDestroyed: false,
    };
  },
  computed: {
    hasValidDates() {
      return this.archiveFrom instanceof Date && this.archiveTo instanceof Date
        && Number.isFinite(+this.archiveFrom) && Number.isFinite(+this.archiveTo)
        && this.archiveFrom <= this.archiveTo;
    },
    canLoadReport() {
      return this.selectedCameraNumbers.length > 0 && this.hasValidDates && (this.videoEnabled || this.audioEnabled);
    },
    reportParams() {
      return {
        cameraNumbers: [...this.selectedCameraNumbers],
        archiveFrom: this.archiveFrom,
        archiveTo: this.archiveTo,
        query: this.searchText,
        sourceType: this.videoEnabled === this.audioEnabled ? undefined : this.videoEnabled ? "video" : "audio",
      };
    },
  },
  watch: {
    // Список камер хранится в URL и восстанавливается при открытии или обновлении страницы.
    initialCameraNumbers(numbers) {
      if (!_.isEqual(numbers, this.selectedCameraNumbers)) {
        this.selectedCameraNumbers = [...numbers];
      }
    },
    archiveFrom: "filtersChanged",
    archiveTo: "filtersChanged",
    searchText: "filtersChanged",
    videoEnabled: "filtersChanged",
    audioEnabled: "filtersChanged",
    selectedCameraNumbers() {
      this.filtersChanged();
      this.loadCameraTitles();
    },
    pageSize: "filtersChanged",
    currentPage: "scheduleReport",
  },
  created() {
    this.debouncedLoadReport = _.debounce(this.loadReport, 350);
    this.loadCameraTitles();
    this.loadReport();
  },
  beforeDestroy() {
    this.isReportDestroyed = true;
    this.requestVersion += 1;
    this.debouncedLoadReport.cancel();
  },
  methods: {
    filtersChanged() {
      this.currentPage = 1;
      this.scheduleReport();
    },
    scheduleReport() {
      // Сразу отменяем применение старого ответа, в том числе во время debounce.
      this.requestVersion += 1;
      this.messages = [];
      this.loadError = "";
      this.isLoadingReport = Boolean(this.canLoadReport);
      this.debouncedLoadReport();
    },
    resetFilters() {
      this.archiveTo = new Date();
      this.archiveFrom = new Date(this.archiveTo.getTime() - 60 * 60 * 1000);
      this.searchText = "";
      this.videoEnabled = true;
      this.audioEnabled = true;
      this.filtersChanged();
    },
    errorText(error) {
      return error?.response?.data?.fields?.non_field_errors?.[0]
        || error?.fields?.non_field_errors?.[0] || this.$t('archiveError');
    },
    async loadReport() {
      const version = ++this.requestVersion;
      this.loadError = "";
      if (!this.canLoadReport) {
        this.messages = [];
        this.allPages = 1;
        this.isLoadingReport = false;
        return;
      }
      this.isLoadingReport = true;
      try {
        const {messages, response, error} = await this.$store.dispatch(REPORT_ACTION, {
          ...this.reportParams, page: this.currentPage, page_size: this.pageSize,
        });
        if (version !== this.requestVersion || this.isReportDestroyed) return;
        if (!response) throw error;
        this.messages = messages || [];
        this.allPages = response.page?.all || 1;
        this.currentPage = Math.min(response.page?.current || this.currentPage, this.allPages);
      } catch (error) {
        if (version !== this.requestVersion || this.isReportDestroyed) return;
        this.messages = [];
        this.loadError = this.errorText(error);
      } finally {
        if (version === this.requestVersion && !this.isReportDestroyed) this.isLoadingReport = false;
      }
    },
    cameraTitle(number) {
      const camera = this.camerasByNumber[number];
      return camera?.address || camera?.title || "—";
    },
    async loadCameraTitles() {
      const numbers = this.selectedCameraNumbers.filter(number => !this.camerasByNumber[number]);
      if (!numbers.length) return;
      try {
        const cameras = await this.$store.dispatch(`cameras/${ACTION_LOAD_CAMERAS}`, {
          numbers, fields: [FIELDS_CAMERA.number, FIELDS_CAMERA.address, FIELDS_CAMERA.title],
        });
        if (this.isReportDestroyed) return;
        cameras.forEach(camera => this.$set(this.camerasByNumber, String(camera.number), camera));
      } catch (error) {
        devLog("[AircraftCamerasReport] Failed to load camera titles", error);
      }
    },
    openCameraSelection() {
      this.$camsdals.open(
        AircraftReportCamerasDialog,
        {initialSelectedCameraNumbers: [...this.selectedCameraNumbers]},
        {dialogTitle: this.$t('selectCameras')},
        {
          closeOnBackdrop: false,
          size: "vuedal-auto-width",
          name: "js-click-outside",
          onClose: (selection) => {
            const numbers = selection?.selectedCameraNumbers;
            if (Array.isArray(numbers) && !this.isReportDestroyed) {
              const cameraNumbers = [...new Set(numbers.map(String))];
              if (_.isEqual(cameraNumbers, this.selectedCameraNumbers)) return;
              this.$router.replace({
                query: {
                  ...this.$route.query,
                  [QUERY_KEY_AIRCRAFT_REPORT_CAMERA_NUMBERS]: cameraNumbers,
                },
              }).catch(error => {
                devLog("[AircraftCamerasReport] Failed to update report cameras", error);
              });
            }
          },
        }
      );
    },
    async openEventVideo(message) {
      this.isLoadingVideo = true;
      try {
        const camera = await this.$store.dispatch(`cameras/${ACTION_LOAD_CAMERA_FOR_PLAYER}`, {
          number: message.cameraNumber, tokenDVRTTL: TOKEN_TTL.PLAYER,
        });
        if (this.isReportDestroyed) return;
        if (!camera || !camera.isAvailableArchive()) throw new Error();
        this.playEventStart(message, camera);
      } catch (error) {
        if (!this.isReportDestroyed) this.$camsdals.alert(this.errorText(error));
      } finally {
        this.isLoadingVideo = false;
      }
    },
    async downloadTableAsCSVReport() {
      if (!this.canLoadReport || this.isExporting || this.isReportDestroyed) return;
      this.isExporting = true;
      // Все страницы выгрузки используют фильтры, выбранные на момент её запуска.
      const params = this.reportParams;
      const rows = [];
      const csvCell = value => `"${String(value ?? "").replace(/"/g, '""')}"`;
      try {
        let page = 1;
        let allPages = 1;
        do {
          const {messages, response, error} = await this.$store.dispatch(REPORT_ACTION, {
            ...params, page, page_size: 1000,
          });
          if (this.isReportDestroyed) return;
          if (!response) throw error;
          if (rows.length + messages.length > MAX_EXPORT_ROWS) {
            this.$camsdals.alert(this.$t('maxRowLimit') + MAX_EXPORT_ROWS);
            return;
          }
          rows.push(...messages.map(message => [
            message.cameraNumber, this.cameraTitle(message.cameraNumber), message.source_type,
            message.text || this.$t('aircraftEvent'), this.$options.filters.localDateTime(message.date),
          ].map(csvCell).join(",")));
          allPages = response.page?.all || 1;
          page += 1;
        } while (page <= allPages);
        const headers = ['cameraNumber', 'cameraAddress', 'eventType', 'text', 'date'].map(key => csvCell(this.$t(key)));
        downloadCSV(`${this.$t('aircraftReport')}.csv`, [headers.join(","), ...rows].join("\r\n"));
      } catch (error) {
        if (!this.isReportDestroyed) this.$camsdals.alert(this.errorText(error));
      } finally {
        this.isExporting = false;
      }
    },
    async downloadTableAsXLSXReport() {
      if (!this.canLoadReport || this.isExporting || this.isReportDestroyed) return;
      this.isExporting = true;
      const {cameraNumbers, archiveFrom, archiveTo, query, sourceType} = this.reportParams;
      try {
        await this.downloadReportAsXLSX(
          "api:v0:analytics-aircraft-report",
          `${this.$t('aircraftReport')}.xlsx`,
          {
            camera_numbers: cameraNumbers,
            start: archiveFrom.toISOString(),
            end: archiveTo.toISOString(),
            query,
            ...(sourceType ? {source_type: sourceType} : {}),
          }
        );
      } catch (error) {
        if (!this.isReportDestroyed) this.$camsdals.alert(this.errorText(error));
      } finally {
        this.isExporting = false;
      }
    },
  },
};
</script>

<style lang="scss">
@use "../../../oneScreen/reports/analytic-report.scss";

.aircraft-cameras-report {
  width: 100%;
  min-width: 0;
  min-height: 300px;

  &__title {
    margin: 0 0 16px;
    font-size: 24px;
  }

  .analytic-report__settings {
    flex-wrap: wrap;
    gap: 12px 0;
  }

  &__sources {
    display: flex;
    flex-wrap: wrap;
    gap: 24px;
    margin-bottom: 20px;
  }

  .analytic-report__table {
    max-height: none;
  }

  @media (max-width: 600px) {
    .analytic-report__pagination {
      flex-wrap: wrap;
      gap: 12px;
    }

    .analytic-report__pagination_smart-pagination {
      padding-left: 0;
    }
  }
}
</style>
